import React from 'react'
import clsx from 'clsx'
import { Additem, Microphone2 } from 'iconsax-react'
import { track } from '@SonarCX/lib/mixpanel'
import { format } from 'date-fns'
import { useTheme } from '@mui/material'

import UploadFile from '../../../../organisms/UploadFile'
import TaskFile from './File'
import MediaViewerCarousel from '@SonarCX/components/molecules/MediaViewerCarousel'

import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'

import useStyles from './styles'

const FileSection = ({
  setValues,
  values,
  setModifiedTask,
  setChanged = val => null,
  onMobile,
  multi,
  enablePastingFiles,
  pasteSelector = '#agentPage'
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const [viewingFile, setViewingFile] = React.useState(null)
  const { setV2, setAction, setSubtext, setTitle } = useAreYouSure()
  const {
    startRecording,
    stopRecording,
    recordingBlob,
    isRecording,
    recordingTime,
    clearRecord
  } = useTaskAPI()

  const isFileArray = Array.isArray(values.file)

  React.useEffect(() => {
    if (recordingBlob) {
      setValues(vals => ({
        ...vals,
        file: [
          ...(vals.file ?? []),
          new File(
            [recordingBlob],
            `Audio note ${format(new Date(), 'MMM d yyyy, h:mm aaa')}.wav`
          )
        ]
      }))
      setChanged(true)
      setModifiedTask && setModifiedTask(false)

      clearRecord()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingBlob, clearRecord, setValues])

  const handleSetFile = React.useCallback(
    f => {
      if (f) {
        setValues(vals => ({
          ...vals,
          file: [...(vals.file ?? []), f]
        }))
        track('TASK_FILE_ADDED', { taskId: values.id ?? 'NEW_TASK' })

        setChanged(true)
        setModifiedTask && setModifiedTask(false)
      }
    },
    [setChanged, setModifiedTask, setValues, values]
  )

  const onRecordClick = React.useCallback(() => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }, [isRecording, stopRecording, startRecording])

  const removeFile = (i, f) => {
    const action = () => {
      track('TASK_FILE_DELETED', { taskId: values.id ?? 'NEW_TASK' })
      // enabling update button
      setModifiedTask && setModifiedTask(false)

      const modified = [...(values.file ?? [])]
      modified.splice(i, 1)
      if (f.id) {
        const newMedia = (values.media ?? []).filter(m => m.id !== f.id)
        setValues(vals => ({ ...vals, media: newMedia }))
      }

      setChanged(true)

      setValues(vals => ({ ...vals, file: modified }))
    }

    setV2(true)
    setTitle('Delete file?')
    setSubtext('Once deleted this file will be gone forever.')
    setAction(() => {
      action()
    })
  }

  return (
    <>
      <div style={{ marginTop: onMobile ? '1rem' : 'unset' }}>
        <div className={clsx(classes.center, classes.mobileCenter)}>
          <div className={clsx(classes.center, classes.attach)}>
            <UploadFile
              className={classes.iconBackground}
              name='Attach File'
              multi={multi}
              enablePastingFiles={enablePastingFiles}
              pasteSelector={pasteSelector}
              icon={
                <Additem
                  color='black'
                  style={{ marginRight: '0.25rem', cursor: 'pointer' }}
                  size={theme.size.base}
                />
              }
              onSetFile={handleSetFile}
            />
          </div>

          <div
            className={clsx(classes.center, classes.addVoice)}
            onClick={onRecordClick}
          >
            <Microphone2
              color={isRecording ? 'red' : 'black'}
              style={{ marginRight: '0.25rem' }}
              size={theme.size.base}
            />
            {isRecording ? (
              <div className={classes.recordingStatus}>
                <div className={classes.recordingDot}></div>
                <label>Recording...</label>
                <label>
                  {Math.floor(recordingTime / 60)}:
                  {String(recordingTime % 60).padStart(2, '0')}
                </label>
              </div>
            ) : (
              <label>Attach voice note</label>
            )}
          </div>
        </div>

        {isFileArray &&
          values.file
            .filter(Boolean)
            .map((f, i) => (
              <TaskFile
                classes={classes}
                file={f}
                index={i}
                key={i}
                removeFile={removeFile}
                setViewingFile={setViewingFile}
              />
            ))}
        {!onMobile && values.file?.length > 0 && (
          <div
            className={classes.center}
            style={{ marginRight: '2rem', cursor: 'default' }}
          >
            <label className={classes.center}>
              {values.file?.length ?? 0} attachment
              {(values.file?.length ?? 0) === 1 ? '' : 's'}
            </label>
          </div>
        )}
      </div>

      {viewingFile && (
        <MediaViewerCarousel
          files={values.file ?? []}
          file={viewingFile}
          onClose={() => setViewingFile(null)}
        />
      )}
    </>
  )
}

export default FileSection
